import React, { useEffect, useState } from 'react';
import AdminSidebar from './AdminSidebar';
import AdminHeader from './AdminHeader';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const base_url = process.env.REACT_APP_API_URL;

const CasinoHistory = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(!(window.innerWidth < 768));
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const fetchHistory = async () => {
    const auth = localStorage.getItem('admin');
    try {
      const response = await axios.get(base_url + '/api/webapi/admin/casinoHistory', {
        headers: {
          Accept: 'application/json',
          Authorization: `${auth}`
        }
      });

      if (response?.data.success) {
        setHistory(response.data.data);
        setFilteredHistory(response.data.data); // Initialize with all data
      }
    } catch (error) {
      console.error("Error fetching casino history:", error);
    }
  };

  // Combined filter function (user ID + date range)
  const applyFilters = (userIdFilter, start, end) => {
    let filtered = history;

    // Filter by User ID
    if (userIdFilter) {
      filtered = filtered.filter(val =>
        val.playerId.toString().includes(userIdFilter)
      );
    }

    // Filter by date range if both dates are provided
    if (start && end) {
      // Convert string dates to Date objects
      const startD = new Date(start);
      const endD = new Date(end);

      filtered = filtered.filter(val => {
        const valDate = new Date(val.time);
        return valDate >= startD && valDate <= endD;
      });
    }

    setFilteredHistory(filtered);
  };

  // Handle user id input change
  const handleUserIdChange = (e) => {
    const value = e.target.value;
    setFilterText(value);
    applyFilters(value, startDate, endDate);
  };

  // Handle start date change
  const handleStartDateChange = (e) => {
    const value = e.target.value;
    setStartDate(value);
    applyFilters(filterText, value, endDate);
  };

  // Handle end date change
  const handleEndDateChange = (e) => {
    const value = e.target.value;
    setEndDate(value);
    applyFilters(filterText, startDate, value);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.text("Casino Bet History", 14, 15);

    const tableData = filteredHistory.map(data => [
      data.playerId,
      data.gameId,
      data.gameCategory,
      data.gameProvider,
      data.amount,
      data.profit,
      data.status,
      data.time
    ]);

    autoTable(doc, {
      startY: 20,
      head: [["User ID", "Game Code", "Game Name", "Provider", "Price", "P/L", "Status", "Date"]],
      body: tableData,
    });

    doc.save("casino-history.pdf");
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchHistory();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setIsSidebarOpen(true);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="wrapper">
      <AdminSidebar
        isSidebarOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        isMobile={isMobile}
      />

      <AdminHeader
        toggleSidebar={toggleSidebar}
        isSidebarOpen={isSidebarOpen}
        isMobile={isMobile}
      />

      <div
        className={`content-wrapper ${!isMobile && isSidebarOpen ? "" : "open-content-wrapper"}`}
        style={{ marginLeft: (!isMobile && isSidebarOpen) ? "" : "0", transition: "margin-left 0.3s ease" }}
      >
        <section className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1>Casino Bets History</h1>
              </div>
            </div>
          </div>
        </section>

        <div className="container-fluid">
          <div className="card">
            <div className="row">
              <div className="col-md-12">
                <div className='m-3 d-flex flex-wrap justify-content-between align-items-center gap-3'>
                  {/* User ID Filter */}
                  <input
                    type='text'
                    placeholder='Enter user id to search'
                    className='form-control'
                    style={{ minWidth: '200px' }}
                    onChange={handleUserIdChange}
                    value={filterText}
                  />
                  {/* Start Date Filter */}
                  
                  <div>
                    <label>From:</label>
                    <input
                    type='date'
                    className='form-control'
                    style={{ minWidth: '160px' }}
                    value={startDate}
                    onChange={handleStartDateChange}
                    max={endDate || ''}
                    placeholder="Start Date"
                  />
                  </div>

                  {/* End Date Filter */}
                  <div>
                    <label>To:</label>
                    <input
                    type='date'
                    className='form-control'
                    style={{ minWidth: '160px' }}
                    value={endDate}
                    onChange={handleEndDateChange}
                    min={startDate || ''}
                    placeholder="End Date"
                  />
                  </div>

                  <button className='btn btn-primary ms-3' onClick={handleDownloadPDF}>
                    Download PDF
                  </button>
                </div>

                <div className='mt-4'>
                  <table className="table table-responsive-sm">
                    <thead>
                      <tr className="p-2">
                        <th className="text-center text-nowrap">User ID</th>
                        <th className="text-center text-nowrap">Game Code</th>
                        <th className="text-center text-nowrap">Game Name</th>
                        <th className="text-center text-nowrap">Provider Name</th>
                        <th className="text-center text-nowrap">Price</th>
                        <th className="text-center text-nowrap">Profit/Loss</th>
                        <th className="text-center text-nowrap">Status</th>
                        <th className="text-center text-nowrap">Place Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHistory.map((data) => (
                        <tr key={data.id}>
                          <td className='text-start'>{data.playerId}</td>
                          <td className='text-start'>{data.gameId}</td>
                          <td className='text-start'>{data.gameCategory}</td>
                          <td className='text-start'>{data.gameProvider}</td>
                          <td className='text-start'>{data.amount}</td>
                          <td className={`text-start ${data?.status === 'lose' ? 'text-red' : 'text-green'}`}>
                            {data.profit}
                          </td>
                          <td className='text-center'>
                            <span className='rounded px-2' style={{
                              background: data?.status === 'lose' ? 'rgb(255 82 82)' : 'rgb(125 233 136)'
                            }}>
                              {data?.status}
                            </span>
                          </td>
                          <td className='text-start'>{data.time}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

              </div>
            </div>
          </div>
        </div>

        <div className="mb-4" />
      </div>
    </div>
  );
};

export default CasinoHistory;
